import {CSSProperties, Fragment, HTMLAttributes, ReactElement, ReactNode, useMemo, useState} from 'react'
import {
    TableBody,
    TableBodyCell,
    TableBodyRow,
    TableBodyRowProps,
    TableColumnResizer,
    TableContainer,
    TableHeader,
    TableHeaderCell,
    TableHeaderCellLabel,
    TablePagination,
    TablePaginationProps,
    TableSortButton,
    TableWrapper
} from '@components/ui/table-atoms/TableAtoms.tsx'
import {
    ColumnOrderState,
    ColumnPinningState,
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
    VisibilityState
} from '@tanstack/react-table'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {Skeleton} from '@components/ui/skeleton/Skeleton.tsx'

export type SorterDirection = 'asc' | 'desc' | null

export interface TableProps<TData extends unknown[], TSortName extends string> extends HTMLAttributes<HTMLDivElement> {
    data: TData
    columns: Array<{
        cell: (props: {
            forwardedProps: {
                cellSize: number
                className?: string
                isPinned: boolean
                isSelected: boolean
                style?: CSSProperties
            }
            dataItem: TData[number]
            index: number
            isRowExpanded: boolean
            toggleExpanded: () => void
        }) => ReactNode
        header: () => ReactNode
        id: string
        maxSize?: (tableWidth: number) => number
        minSize?: (tableWidth: number) => number
        size: (tableWidth: number) => number
        sortName?: TSortName
    }>
    columnOrder?: ColumnOrderState //array of column ids
    columnPinning?: ColumnPinningState //the values in the left and right arrays are the column ids
    columnVisibility?: VisibilityState //the keys of this Record are the column ids
    emptyStateComponent?: ReactElement
    expandedRow?: (row: TData[number]) => ReactNode
    getIsRowSelected?: (props: {dataItem?: TData[number]}) => boolean
    isLoading?: boolean
    rowComponent?: (options: {
        tableBodyRowProps: TableBodyRowProps
        dataItem?: TData[number]
        index: number
    }) => ReactNode
    pagination?: TablePaginationProps['pagination']
    sorter?: {
        sortName: TSortName | null
        direction: SorterDirection
        onSort: (sortName: TSortName | null, direction: SorterDirection) => void
    }
    subRow?: (row: TData[number]) => ReactNode
}

interface ColumnMeta<TSortName extends string> {
    sortName: TSortName
}

const makeReactTableColumns = <TData extends unknown[], TSortName extends string>(
    columns: TableProps<TData, TSortName>['columns'],
    tableWidth: number,
    getIsRowSelected?: TableProps<TData, TSortName>['getIsRowSelected']
) => {
    const columnHelper = createColumnHelper<TData[number]>()

    return columns.map(column =>
        columnHelper.accessor(() => column.id, {
            id: column.id,
            cell: cell => {
                const pinningPosition = cell.column.getIsPinned()

                return column.cell({
                    forwardedProps: {
                        className: pinningPosition ? 'pinned' : undefined,
                        isPinned: !!pinningPosition,
                        isSelected: getIsRowSelected?.({dataItem: cell.row.original}) ?? false,
                        cellSize: cell.column.getSize(),
                        style:
                            pinningPosition == 'left'
                                ? {
                                      position: 'sticky' as const,
                                      left: cell.column.getStart('left')
                                  }
                                : pinningPosition == 'right'
                                  ? {
                                        position: 'sticky' as const,
                                        right: cell.column.getAfter('right')
                                    }
                                  : undefined
                    },
                    dataItem: cell.row.original,
                    index: cell.row.index,
                    isRowExpanded: cell.row.getIsExpanded(),
                    toggleExpanded: () => cell.row.toggleExpanded(!cell.row.getIsExpanded())
                })
            },
            enableHiding: true,
            enableResizing: !!column.minSize || !!column.maxSize,
            header: () => column.header(),
            minSize: column.minSize?.(tableWidth),
            maxSize: column.maxSize?.(tableWidth),
            size: column.size(tableWidth),
            meta: {
                sortName: column.sortName
            }
        })
    )
}

export const Table = <TData extends unknown[], TSortName extends string>({
    data,
    columnOrder,
    columnPinning = {},
    columns,
    columnVisibility,
    emptyStateComponent,
    expandedRow,
    getIsRowSelected,
    isLoading,
    pagination,
    rowComponent,
    sorter,
    subRow,
    ...rest
}: TableProps<TData, TSortName>) => {
    const [tableContainerRef, setTableContainerRef] = useState<HTMLDivElement | null>(null)
    const tableData = useMemo(
        () => (isLoading ? Array.from({length: pagination?.limit ?? 50}) : data),
        [data, isLoading]
    )

    const table = useReactTable({
        data: tableData,
        getRowId: (row, index) =>
            row && typeof row == 'object' && 'id' in row && row.id ? row.id.toString() : index.toString(),
        columns: makeReactTableColumns(columns, tableContainerRef?.scrollWidth ?? 0, getIsRowSelected),
        columnResizeMode: 'onChange',
        enableExpanding: !!expandedRow,
        getExpandedRowModel: getExpandedRowModel(),
        getCoreRowModel: getCoreRowModel(),
        state: {
            columnOrder,
            columnPinning,
            columnVisibility
        }
    })

    return (
        <TableWrapper {...rest}>
            {tableData.length == 0 ? (
                emptyStateComponent
            ) : (
                <TableContainer hasPagination={!!pagination} ref={setTableContainerRef}>
                    <TableHeader>
                        {table.getHeaderGroups().map(headerGroup => (
                            <Fragment key={headerGroup.id}>
                                {headerGroup.headers.map(header => {
                                    const columnMeta = header.column.columnDef.meta as ColumnMeta<TSortName>
                                    const pinningPosition = header.column.getIsPinned()
                                    return (
                                        <TableHeaderCell
                                            isPinned={!!pinningPosition}
                                            cellSize={header.getSize()}
                                            key={header.id}
                                            style={
                                                pinningPosition == 'left'
                                                    ? {
                                                          position: 'sticky',
                                                          zIndex: 2,
                                                          left: header.column.getStart('left')
                                                      }
                                                    : pinningPosition == 'right'
                                                      ? {
                                                            position: 'sticky',
                                                            zIndex: 2,
                                                            right: header.column.getAfter('right')
                                                        }
                                                      : undefined
                                            }
                                        >
                                            <TableHeaderCellLabel>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </TableHeaderCellLabel>
                                            {!!sorter && !!columnMeta.sortName && (
                                                <TableSortButton sortName={columnMeta.sortName} sorter={sorter} />
                                            )}
                                            {header.column.getCanResize() && <TableColumnResizer header={header} />}
                                        </TableHeaderCell>
                                    )
                                })}
                            </Fragment>
                        ))}
                    </TableHeader>
                    <TableBody
                        data={table.getRowModel().rows}
                        rowRender={(row, index) => {
                            const isRowSelected = !!getIsRowSelected?.({dataItem: row.original})
                            const children = (
                                <Fragment key={row.id}>
                                    <Flexbox fullWidth>
                                        {row.getVisibleCells().map(cell => (
                                            <Fragment key={`${cell.row.id}-${cell.id}`}>
                                                {flexRender(
                                                    isLoading
                                                        ? () => (
                                                              <TableBodyCell
                                                                  isPinned={!!cell.column.getIsPinned()}
                                                                  cellSize={cell.column.getSize()}
                                                              >
                                                                  <Skeleton height="32px" />
                                                              </TableBodyCell>
                                                          )
                                                        : cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </Fragment>
                                        ))}
                                    </Flexbox>
                                    {row.getIsExpanded() && expandedRow?.(row.original)}
                                    {!!subRow && subRow(row.original)}
                                </Fragment>
                            )
                            return rowComponent ? (
                                rowComponent({
                                    tableBodyRowProps: {children, isSelected: isRowSelected},
                                    dataItem: row.original,
                                    index
                                })
                            ) : (
                                <TableBodyRow isSelected={isRowSelected} key={row.id}>
                                    {children}
                                </TableBodyRow>
                            )
                        }}
                    />
                </TableContainer>
            )}
            {!!pagination && <TablePagination pagination={pagination} />}
        </TableWrapper>
    )
}
